import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, UserPlus, Link2, Search, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { whatsappAPI } from './whatsappAPI';
import { fmtTelefone } from './organizacao';

/* Contato que ainda não é cliente: o atendente cadastra (preenche os dados principais) ou vincula a um
 * cliente que já existe. Pedir o cadastro ao próprio contato fica no botão ao lado, na ficha. */

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
  'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const onlyDigits = (s) => String(s || '').replace(/\D/g, '');
const fmtDoc = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length <= 11) return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
};
const inp = 'w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40';
const lbl = 'block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1';

export default function ContatoClienteModal({ conv, abaInicial = 'cadastrar', onClose, onFeito }) {
  const [aba, setAba] = useState(abaInicial);
  const [criado, setCriado] = useState(null);   // cliente_id depois de cadastrar
  const telConv = conv?.numero ? fmtTelefone(conv.numero) : '';
  const [form, setForm] = useState({
    nome_completo: conv?.cliente_nome || conv?.push_name || '', cpf: '', telefone: telConv,
    data_nascimento: '', cidade: '', estado: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (aba !== 'vincular') return undefined;
    const termo = q.trim();
    if (termo.length < 2) { setRes(null); return undefined; }
    const n = ++seq.current;
    const t = setTimeout(async () => {
      setBuscando(true);
      try { const r = await whatsappAPI.buscarClientes(termo); if (n === seq.current) setRes(r.data || []); }
      catch (_) { if (n === seq.current) setRes([]); }
      finally { if (n === seq.current) setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, aba]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: k === 'cpf' ? fmtDoc(e.target.value) : e.target.value }));

  const cadastrar = async (e) => {
    e.preventDefault();
    if (form.nome_completo.trim().length < 3) { toast.error('Informe o nome completo'); return; }
    setSalvando(true);
    try {
      const r = await whatsappAPI.criarCliente(conv.jid, { ...form, cpf: onlyDigits(form.cpf) });
      toast.success('Cliente cadastrado e vinculado à conversa');
      setCriado(r.data.cliente_id);
      onFeito?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Não foi possível cadastrar'); }
    finally { setSalvando(false); }
  };

  const vincular = async (cli) => {
    setSalvando(true);
    try {
      await whatsappAPI.vincularCliente(conv.jid, cli.id);
      toast.success(`Conversa vinculada a ${cli.nome_completo}`);
      onFeito?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Não foi possível vincular'); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}
        data-testid="wa-cliente-modal">
        <div className="px-5 py-4 border-b dark:border-slate-800 flex items-center justify-between">
          <h3 className="font-semibold">Cliente desta conversa</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>

        {criado ? (
          <div className="px-5 py-8 text-center space-y-3" data-testid="wa-cliente-criado">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="font-medium">Cliente cadastrado</p>
            <p className="text-sm text-slate-500">Endereço, dados do gov.br e bancários podem ser completados na página do cliente.</p>
            <div className="flex justify-center gap-2 pt-1">
              <button onClick={onClose} className="h-9 px-4 rounded-lg border text-sm">Fechar</button>
              <Link to={`/clientes/${criado}`} className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium flex items-center gap-1.5">
                <ExternalLink className="w-4 h-4" /> Completar cadastro
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 pt-3">
              <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">
                {[['cadastrar', 'Cadastrar novo', UserPlus], ['vincular', 'Já é cliente', Link2]].map(([k, l, Icon]) => (
                  <button key={k} onClick={() => setAba(k)} data-testid={`wa-cliente-aba-${k}`}
                    className={`flex-1 h-8 rounded-md font-medium flex items-center justify-center gap-1.5 ${aba === k ? 'bg-white dark:bg-slate-900 shadow-sm' : 'text-slate-500'}`}>
                    <Icon className="w-3.5 h-3.5" />{l}
                  </button>
                ))}
              </div>
            </div>

            {aba === 'cadastrar' ? (
              <form onSubmit={cadastrar} className="px-5 py-4 space-y-3">
                <div>
                  <label className={lbl} htmlFor="wa-cli-nome">Nome completo *</label>
                  <input id="wa-cli-nome" className={inp} value={form.nome_completo} onChange={set('nome_completo')} required autoFocus data-testid="wa-cli-nome" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl} htmlFor="wa-cli-cpf">CPF/CNPJ</label>
                    <input id="wa-cli-cpf" className={inp} value={form.cpf} onChange={set('cpf')} inputMode="numeric" data-testid="wa-cli-cpf" />
                  </div>
                  <div>
                    <label className={lbl} htmlFor="wa-cli-nasc">Nascimento</label>
                    <input id="wa-cli-nasc" type="date" className={inp} value={form.data_nascimento} onChange={set('data_nascimento')} />
                  </div>
                </div>
                <div>
                  <label className={lbl} htmlFor="wa-cli-tel">Telefone</label>
                  <input id="wa-cli-tel" className={inp} value={form.telefone} onChange={set('telefone')} placeholder="(65) 99999-9999" data-testid="wa-cli-tel" />
                </div>
                <div className="grid grid-cols-[1fr_88px] gap-3">
                  <div>
                    <label className={lbl} htmlFor="wa-cli-cidade">Cidade</label>
                    <input id="wa-cli-cidade" className={inp} value={form.cidade} onChange={set('cidade')} />
                  </div>
                  <div>
                    <label className={lbl} htmlFor="wa-cli-uf">UF</label>
                    <select id="wa-cli-uf" className={inp} value={form.estado} onChange={set('estado')}>
                      <option value="">—</option>
                      {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border text-sm">Cancelar</button>
                  <button type="submit" disabled={salvando} data-testid="wa-cli-salvar"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
                    {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Cadastrar e vincular
                  </button>
                </div>
              </form>
            ) : (
              <div className="px-5 py-4">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={q} onChange={e => setQ(e.target.value)} autoFocus placeholder="Nome, CPF ou telefone do cliente"
                    className={`${inp} pl-9`} data-testid="wa-cli-busca" />
                </div>
                <div className="mt-2 max-h-64 overflow-y-auto -mx-2">
                  {buscando && <p className="px-2 py-3 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando…</p>}
                  {!buscando && res?.length === 0 && <p className="px-2 py-3 text-sm text-slate-500">Nenhum cliente encontrado.</p>}
                  {!buscando && res?.map(c => (
                    <button key={c.id} onClick={() => vincular(c)} disabled={salvando} data-testid={`wa-cli-res-${c.id}`}
                      className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3">
                      <span className="flex-1 min-w-0">
                        <span className="text-sm font-medium block truncate">{c.nome_completo}</span>
                        <span className="text-xs text-slate-500 block truncate">
                          {[c.cpf && fmtDoc(c.cpf), c.telefone, [c.cidade, c.estado].filter(Boolean).join('/')].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400 shrink-0">Vincular</span>
                    </button>
                  ))}
                  {!res && !buscando && <p className="px-2 py-3 text-xs text-slate-500">Digite pelo menos 2 letras ou números.</p>}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
